import React, { useState } from 'react';
import styled from 'styled-components';
import AvailabilityCalendar from './AvailabilityCalendar';
import Button from './Button';

const BookingContainer = styled.div`
  background: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  border: 1px solid #e1e5e9;
`;

const CalendarSection = styled.div`
  margin: 1.5rem 0;
`;

const Label = styled.label`
  display: block;
  font-weight: 600;
  color: #333;
  margin-bottom: 0.5rem;
`;

const DateSummary = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background: rgba(0, 71, 68, 0.12);
  border-radius: 8px;
  border: 2px solid #004744;
  margin-bottom: 1rem;
  
  .date-item {
    text-align: center;
    
    .label {
      font-size: 0.8rem;
      color: #666;
      margin-bottom: 0.25rem;
    }
    
    .date {
      font-weight: 600;
      color: #004744;
      font-size: 0.9rem;
    }
  }
  
  .nights {
    text-align: center;
    font-weight: 600;
    color: #004744;
    
    .number {
      font-size: 1.5rem;
      display: block;
    }
    
    .text {
      font-size: 0.8rem;
    }
  }
`;

interface BookingFormProps {
  accommodationId: string;
  accommodationTitle: string;
  basePrice: number;
  maxGuests: number;
  airbnbListingId?: string | null;
}

const BookingForm: React.FC<BookingFormProps> = ({
  accommodationId,
  accommodationTitle,
  airbnbListingId
}) => {
  const [dateRange, setDateRange] = useState<{ checkIn: string | null; checkOut: string | null }>({ checkIn: null, checkOut: null });

  const today = new Date().toISOString().split('T')[0];

  const handleDateRangeSelect = (checkIn: string, checkOut: string) => {
    setDateRange({ checkIn, checkOut });
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('it-IT', {
      weekday: 'short',
      day: 'numeric',
      month: 'short'
    });
  };

  const nights = (() => {
    if (!dateRange.checkIn || !dateRange.checkOut) return 0;
    const start = new Date(dateRange.checkIn);
    const end = new Date(dateRange.checkOut);
    const diff = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
    return diff > 0 ? diff : 0;
  })();

  const airbnbUrl = airbnbListingId
    ? `https://www.airbnb.it/rooms/${airbnbListingId}`
    : undefined;

  return (
    <BookingContainer>
      <h3 style={{ marginBottom: '1.5rem', color: '#333' }}>
        {`Date disponibili per "${accommodationTitle}"`}
      </h3>

      <CalendarSection>
        <Label>Seleziona Date</Label>

        {dateRange.checkIn && dateRange.checkOut && (
          <DateSummary>
            <div className="date-item">
              <div className="label">Check-in</div>
              <div className="date">{formatDate(dateRange.checkIn)}</div>
            </div>
            <div className="nights">
              <span className="number">{nights}</span>
              <span className="text">nott{nights === 1 ? 'e' : 'i'}</span>
            </div>
            <div className="date-item">
              <div className="label">Check-out</div>
              <div className="date">{formatDate(dateRange.checkOut)}</div>
            </div>
          </DateSummary>
        )}

        <AvailabilityCalendar
          accommodationId={accommodationId}
          selectedRange={dateRange}
          onDateSelect={handleDateRangeSelect}
          minDate={today}
        />
      </CalendarSection>

      <div style={{ marginTop: '1rem' }}>
        <Button
          variant="green"
          size="large"
          fullWidth
          onClick={() => {
            if (airbnbUrl) {
              window.open(airbnbUrl, '_blank', 'noopener,noreferrer');
            }
          }}
          disabled={!airbnbUrl}
        >
          PRENOTA CON AIRBNB
        </Button>
        {!airbnbUrl && (
          <p style={{ marginTop: '0.5rem', color: '#666', fontSize: '0.9rem', textAlign: 'center' }}>
            ID Airbnb non configurato per questo alloggio.
          </p>
        )}
      </div>
    </BookingContainer>
  );
};

export default BookingForm; 